import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

const ABOUT = {
  en: {
    eyebrow: "The creator",
    name: "Carine Schackt",
    role: "Creator of The Things We Keep",
    p1: "I have always collected things.\nNot because of what they are worth, but because of what they carry.\nA piece of wood shaped by time. Antlers carried through a landscape. A fragment saved because someone, somewhere, couldn't quite let it go.",
    p2: "I am drawn to the things that already have a life before they reach my hands.\nI don't create to make something perfect.\nI create to make something meaningful.",
    p3: "I believe the things we surround ourselves with can carry more than beauty. They can carry memory, connection and pieces of who we are.\nThe Things We Keep grew from that belief.",
    cta: "Create together",
    imageAlt: "Carine Schackt in a misty Norwegian forest, wearing cream knitwear",
    imageCaption: "In the landscape.",
  },
  no: {
    eyebrow: "Skaperen",
    name: "Carine Schackt",
    role: "Skaperen bak The Things We Keep",
    p1: "Jeg har alltid samlet på ting.\nIkke på grunn av hva de er verdt, men på grunn av hva de bærer på.\nEt stykke tre formet av tiden. Gevir båret gjennom et landskap. Et fragment bevart fordi noen, et sted, ikke helt klarte å slippe det.",
    p2: "Jeg trekkes mot ting som allerede har et liv før de når hendene mine.\nJeg lager ikke for å gjøre noe perfekt.\nJeg lager for å gjøre noe meningsfullt.",
    p3: "Jeg tror tingene vi omgir oss med kan bære mer enn skjønnhet. De kan bære minner, tilhørighet og biter av hvem vi er.\nThe Things We Keep vokste ut av den troen.",
    cta: "Start en bestilling",
    imageAlt: "Carine Schackt i tåket norsk skog, med kremfarget strikkegenser",
    imageCaption: "I landskapet.",
  },
};

export default function AboutPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const t = ABOUT[lang];

  useEffect(() => {
    document.title = lang === "no" ? "Om — The Things We Keep" : "About — The Things We Keep";
    return () => {
      document.title =
        lang === "no"
          ? "The Things We Keep — Gjenstander med en historie"
          : "The Things We Keep — Objects with a story";
    };
  }, [lang]);

  return (
    <div className="bg-paper pb-24 pt-32 sm:pb-32 sm:pt-40" data-testid="about-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="ttwk-label" data-testid="about-eyebrow">
                {t.eyebrow}
              </p>
              <h1 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-6xl" data-testid="about-name">
                {t.name}
              </h1>
              <p className="mt-3 font-serif text-2xl italic text-terracotta">{t.role}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="img-frame mt-12 overflow-hidden rounded-sm border border-clay/60">
                <img
                  src="/media/portrait.jpg"
                  alt={t.imageAlt}
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                  data-testid="about-portrait"
                />
              </div>
              <p className="mt-4 font-serif text-sm italic text-umber/60">{t.imageCaption}</p>
            </Reveal>
          </div>

          <div className="flex flex-col justify-center lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <p className="font-serif text-xl font-light leading-[1.6] text-umber sm:text-3xl sm:leading-relaxed" data-testid="about-p1">
                {t.p1.split("\n").map((line, j) => (
                  <span key={j}>
                    {j > 0 && <br />}
                    {line}
                  </span>
                ))}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-10 font-serif text-xl font-light leading-[1.6] text-umber sm:text-3xl sm:leading-relaxed" data-testid="about-p2">
                {t.p2.split("\n").map((line, j) => (
                  <span key={j}>
                    {j > 0 && <br />}
                    {line}
                  </span>
                ))}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-10 font-serif text-xl font-light leading-[1.6] text-umber sm:text-3xl sm:leading-relaxed" data-testid="about-p3">
                {t.p3.split("\n").map((line, j) => (
                  <span key={j}>
                    {j > 0 && <br />}
                    {line}
                  </span>
                ))}
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <button
                onClick={() => navigate("/", { state: { scrollTo: "commission" } })}
                className="mt-14 self-start rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
                data-testid="about-cta"
              >
                {t.cta}
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}